import React from "react";
import { Box, Typography, Button, Container, Grid } from "@mui/material";
import { useRouteError, useNavigate } from "react-router-dom";
import { motion, useAnimation, Variants } from "framer-motion";
import ErrorIllustration from "./assets/error-illustration.svg";
import Lottie from "react-lottie";
import animationData from "../assets/illustrations/error.json";

export const ErrorBoundary = () => {
  const error = useRouteError() as Error;
  const navigate = useNavigate();
  const controls = useAnimation();

  React.useEffect(() => {
    controls.start("visible");
  }, [controls]);

  const handleReset = () => {
    navigate("/");
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.05,
        delayChildren: 0,
      },
    },
  };

  const floatingVariants: Variants = {
    hidden: { y: 0 },
    visible: {
      y: [-5, 5, -5],
      transition: {
        duration: 3,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse",
      },
    },
  };

  const particleVariants: Variants = {
    hidden: { opacity: 0.2, scale: 0.9 },
    visible: {
      opacity: [0.2, 0.5, 0.2],
      scale: [0.9, 1.1, 0.9],
      transition: {
        duration: 2,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "mirror",
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  return (
    <Container maxWidth="lg">
      <motion.div
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <Box
          display="flex"
          minHeight="100vh"
          alignItems="center"
          justifyContent="center"
          py={4}
          sx={{ position: "relative", overflow: "hidden" }}
        >
          <Grid
            container
            spacing={4}
            alignItems="center"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Grid item xs={12} md={6}>
              <motion.div variants={containerVariants}>
                <Lottie
                  options={{
                    animationData: animationData,
                    loop: true,
                    autoplay: true,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                  width={540}
                  height={540}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                  }}
                />
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={containerVariants}>
                <Box>
                  <motion.div variants={textVariants}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 700,
                        color: "error.main",
                        mb: 2,
                        fontSize: { xs: "2rem", md: "3rem" },
                      }}
                    >
                      Oops!
                    </Typography>
                  </motion.div>
                  <motion.div variants={textVariants}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 500,
                        mb: 3,
                        color: "text.primary",
                      }}
                    >
                      Something went wrong
                    </Typography>
                  </motion.div>
                  <motion.div variants={textVariants}>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 4,
                        color: "text.secondary",
                        fontSize: "1.1rem",
                      }}
                    >
                      {error?.message ||
                        "An unexpected error occurred. We're working on fixing this issue."}
                    </Typography>
                  </motion.div>
                  <motion.div variants={textVariants}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <motion.div
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleReset}
                          sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontSize: "1.1rem",
                            backgroundColor: "#0891B2",
                            "&:hover": {
                              backgroundColor: "#0891B2",
                              filter: "brightness(0.9)",
                            },
                          }}
                        >
                          Return Home
                        </Button>
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <Button
                          variant="outlined"
                          size="large"
                          onClick={() => window.location.reload()}
                          sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontSize: "1.1rem",
                            borderColor: "#0891B2",
                            color: "#0891B2",
                            "&:hover": {
                              borderColor: "#0891B2",
                              backgroundColor: "rgba(8, 145, 178, 0.04)",
                            },
                          }}
                        >
                          Try Again
                        </Button>
                      </motion.div>
                    </Box>
                  </motion.div>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
      </motion.div>
    </Container>
  );
};
